// Swedish-specific place type parameters
export const getSwedishPlaceTypeParameters = (placeType) => {
  const swedishPlaceTypes = {
    'preschool': {
      baseType: 'school',
      keywords: ['förskola', 'förskolan', 'dagis', 'förskolor', 'kindergarten', 'preschool', 'montessori'],
      nameFilters: ['förskola', 'förskolan', 'dagis', 'preschool', 'kindergarten', 'montessori'],
      regexPatterns: [
        /förskola(n)?/i,
        /dagis/i,
        /kindergarten/i,
        /montessori/i
      ]
    },
    'primary_school': {
      baseType: 'school',
      keywords: ['grundskola', 'grundskolan', 'lågstadieskola', 'mellanstadieskola', 'högstadieskola', 'primary school'],
      nameFilters: ['grundskola', 'grundskolan', 'lågstadie', 'mellanstadie', 'högstadie', 'primary'],
      regexPatterns: [
        /grundskola(n)?/i,
        /låg(stadie)?/i,
        /mellan(stadie)?/i,
        /hög(stadie)?/i
      ]
    },
    'secondary_school': {
      baseType: 'school',
      keywords: ['gymnasieskola', 'gymnasieskolan', 'gymnasium', 'high school', 'secondary school'],
      nameFilters: ['gymnasie', 'gymnasium', 'high school', 'secondary'],
      regexPatterns: [
        /gymnasi(e|um|eskola|eskolan)/i
      ]
    },
    'systembolaget': {
      baseType: 'store',
      keywords: ['systembolaget', 'liquor store'],
      nameFilters: ['systembolaget', 'system bolaget'],
      regexPatterns: [
        /systembolaget/i
      ]
    }
  };

  return swedishPlaceTypes[placeType] || null;
};

// Filter place results for Swedish-specific place types
export const filterSwedishPlaceResults = (results, placeType) => {
  const typeParams = getSwedishPlaceTypeParameters(placeType);
  if (!typeParams) {
    return results;
  }

  return results.filter(place => {
    // Perfect match for Systembolaget
    if (place.name === 'Systembolaget' && placeType === 'systembolaget') {
      return true;
    }

    const placeName = place.name.toLowerCase();

    // Check name filters
    if (typeParams.nameFilters.some(filter => placeName.includes(filter.toLowerCase()))) {
      return true;
    }

    // Check regex patterns
    if (typeParams.regexPatterns && typeParams.regexPatterns.some(pattern => pattern.test(placeName))) {
      return true;
    }

    // Check types array
    if (place.types && place.types.some(type => {
      const typeWords = type.replace(/_/g, ' ');
      return typeParams.keywords.some(keyword => typeWords.includes(keyword.toLowerCase()));
    })) {
      return true;
    }

    // Check vicinity for school types
    if (place.vicinity) {
      const vicinity = place.vicinity.toLowerCase();

      if (placeType === 'preschool' &&
          (vicinity.includes('förskola') || vicinity.includes('förskolan') || vicinity.includes('dagis'))) {
        return true;
      }

      if (placeType === 'primary_school' &&
          (vicinity.includes('grundskola') || vicinity.includes('skola') ||
          vicinity.includes('lågstadie') || vicinity.includes('mellanstadie') || vicinity.includes('högstadie'))) {
        return true;
      }

      if (placeType === 'secondary_school' &&
          (vicinity.includes('gymnasie') || vicinity.includes('gymnasium'))) {
        return true;
      }
    }

    return false;
  });
};

// Calculate straight-line distance between two points
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

// Filter places by actual distance
export const filterPlacesByActualDistance = (places, center, maxDistanceMeters) => {
  return places.filter(place => {
    if (!place.geometry || !place.geometry.location) {
      return false;
    }

    const lat = typeof place.geometry.location.lat === 'function'
      ? place.geometry.location.lat()
      : place.geometry.location.lat;
    const lng = typeof place.geometry.location.lng === 'function'
      ? place.geometry.location.lng()
      : place.geometry.location.lng;

    const distance = calculateDistance(center.lat, center.lng, lat, lng);

    place.actualDistanceMeters = distance;
    place.actualDistance = distance < 1000
      ? `${Math.round(distance)} m`
      : `${(distance / 1000).toFixed(1)} km`;

    return distance <= maxDistanceMeters;
  });
};
